/**
 * TypeScript interfaces mirroring backend types from web/backend/src/billing/plans.ts
 */

/**
 * Subscription plan tier names
 */
export enum PlanTier {
  /** App-side free tier: no card, no Shopify charge to approve */
  FREE = 'FREE',
  STARTER = 'STARTER',
  GROWTH = 'GROWTH',
  BUSINESS = 'BUSINESS',
}

/**
 * Usage-based pricing beyond a plan's included quota, billed through a Shopify
 * usage line item capped at an amount the merchant approves. Null on the free
 * tier, which stops rather than charging.
 */
export interface OveragePricing {
  pricePerTryOn: number;
  defaultCappedAmount: number;
}

/**
 * Subscription plan configuration
 */
export interface SubscriptionPlan {
  id: PlanTier;
  name: string;
  description: string;
  price: number;
  currency: 'USD';
  trialDays?: number;
  includedTryOns: number;
  overage: OveragePricing | null;
  features: string[];
  active: boolean;
  sortOrder: number;
}

/**
 * Merchant subscription status. Backend stores uppercase (mirrors Shopify),
 * but normalizing both casings here is more forgiving for any legacy records.
 */
export type SubscriptionStatus =
  | 'ACTIVE'
  | 'TRIAL'
  | 'CANCELLED'
  | 'FROZEN'
  | 'PENDING'
  | 'active'
  | 'cancelled'
  | 'frozen'
  | 'pending';

export interface MerchantSubscription {
  shop: string;
  subscriptionId: string;
  planTier: PlanTier;
  status: SubscriptionStatus;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialDays?: number;
  trialEndsAt?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * True if the subscription currently grants the merchant access to a plan.
 * ACTIVE and TRIAL both count; everything else (cancelled, frozen, pending) does not.
 */
export function isSubscriptionActive(
  subscription: MerchantSubscription | null | undefined
): boolean {
  if (!subscription) return false;
  const normalized = subscription.status?.toString().toUpperCase();
  return normalized === 'ACTIVE' || normalized === 'TRIAL';
}

/**
 * Usage statistics for current billing period
 */
export interface UsageStats {
  shop: string;
  planTier: PlanTier;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  /** Effective allowance for this period: during the trial this is the try-on cap, not the plan quota */
  includedTryOns: number;
  usedTryOns: number;
  remainingTryOns: number;
  daysRemaining: number;
  /** True while the merchant is inside the free trial */
  isTrial: boolean;
  /** Try-ons the trial allows in total, or null outside a trial */
  trialCap: number | null;
  /** Trial try-ons still available, or null outside a trial */
  trialTryOnsRemaining: number | null;
  /** True when the merchant approved a usage line item and try-ons continue past the quota */
  overageEnabled: boolean;
  overagePricePerTryOn: number | null;
  overageCappedAmount: number | null;
  /** Try-ons already billed as overage this period */
  overageTryOns: number;
  /** Money already billed as overage this period */
  overageSpend: number;
}

/**
 * Unified Aisthetix Effectiveness report. Mirrors the backend
 * EffectivenessReport in services/attribution.ts.
 */
export interface EffectivenessReport {
  shop: string;
  windowDays: number;
  generatedAt: string;
  funnel: {
    viewers: number;
    tryOnUsers: number;
    findMySizeUsers: number;
    usedAisthetixUsers: number;
    addedToCart: number;
    purchasers: number;
  };
  usage: {
    tryOnOnly: number;
    findMySizeOnly: number;
    both: number;
    usedAisthetix: number;
  };
  attribution: {
    attributedOrders: number;
    attributedRevenue: number;
    currency: string | null;
  };
  comparison: {
    usedCohort: number;
    usedConverted: number;
    usedRate: number;
    baselineCohort: number;
    baselineConverted: number;
    baselineRate: number;
    rateDifference: number;
    liftRatio: number | null;
    ratioComputable: boolean;
    minCohort: number;
    sufficientData: boolean;
  };
  byProduct: Array<{
    productId: string;
    usedCohort: number;
    usedConverted: number;
    usedRate: number;
    baselineCohort: number;
    baselineConverted: number;
    baselineRate: number;
    rateDifference: number;
    liftRatio: number | null;
    ratioComputable: boolean;
    minCohort: number;
    sufficientData: boolean;
  }>;
}

/**
 * Generic API response wrapper
 */
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export type BillingBusinessType = 'company' | 'sole_trader';

export interface MerchantBillingProfile {
  businessType: BillingBusinessType;
  legalName: string;
  taxId: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  region: string;
  postalCode: string;
  countryCode: string;
}

export interface BillingProfileResponse {
  profile: MerchantBillingProfile;
  complete: boolean;
}

/**
 * Subscribe action response
 */
export interface SubscribeResponse {
  /** Null for the free plan, which needs no merchant approval and is applied immediately */
  confirmationUrl: string | null;
  subscriptionId?: string;
  planTier?: PlanTier;
}

/**
 * Page names for navigation
 */
export type PageName = 'dashboard' | 'subscription' | 'usage' | 'analytics' | 'settings';

/**
 * In-app App Store review prompt. Mirrors the backend
 * services/reviewPrompt.ts contract; the caps live on the server.
 */
export type ReviewPromptOutcome = 'snoozed' | 'dismissed' | 'requested' | 'unavailable';

export interface ReviewPromptStatus {
  eligible: boolean;
  attributedRevenue: number;
  currency: string | null;
}

/**
 * Targeting and size guide types. Mirror
 * web/backend/src/models/merchantSettings.ts, with dates as the ISO strings
 * JSON actually carries.
 */
export type FitPreference = 'tight' | 'regular' | 'loose';

export type TargetingMode = 'all' | 'include' | 'exclude';

export const TARGETING_MODES: readonly TargetingMode[] = ['all', 'include', 'exclude'];

/** Mirrors MAX_TARGETING_LIST_LENGTH in web/backend/src/routes/settings.ts,
 * which rejects a longer list outright. */
export const MAX_TARGETING_LIST_LENGTH = 250;

/**
 * `categories` holds taxonomy values compared by value, not by kind: the
 * storefront gate matches each entry against the product's collection
 * handles, its product type and its tags. The admin only ever writes
 * collection handles into it, through the resource picker.
 */
export interface TargetingRule {
  mode: TargetingMode;
  categories: string[];
  /**
   * Bare numeric ids (`7712345`), never GIDs: the Liquid gate compares them
   * against `product.id`. See normalizeProductId in TargetingSection.
   */
  productIds: string[];
}

/** Independent rules: a merchant may run the try-on far wider than the size. */
export interface Targeting {
  tryon: TargetingRule;
  size: TargetingRule;
}

/** Canonical measurement keys the engine emits, all in centimetres. */
export type CanonicalMeasurements = Record<string, number | null | undefined>;

export interface SizeGuideRow {
  size_label: string;
  measurements: CanonicalMeasurements;
}

/**
 * `measurement_basis` decides whether the feature runs at all: the engine
 * compares guide rows directly against the shopper's body, with no body-to
 * -garment ease, so it refuses to serve a size unless the basis is `body`.
 */
export interface StructuredSizeGuide {
  rows: SizeGuideRow[];
  source_unit: 'cm' | 'in';
  measurement_basis: 'body' | 'garment' | 'unknown';
  parsedAt: string;
}

/**
 * The three natures a guide's categories are scoped to, and the precedence
 * (product type > collection > tag) the resolver applies among guides that
 * each claim a different one. Mirrors SizeGuideCategories in
 * web/backend/src/models/merchantSettings.ts. All three empty makes the
 * guide the shop's fallback.
 */
export interface SizeGuideCategories {
  /** Canonical decimal product IDs; absent on legacy guides. */
  productIds?: string[];
  productTypes: string[];
  collections: string[];
  tags: string[];
}

/**
 * One size guide as the admin sees it. Mirrors toSizeGuideResponse() in
 * web/backend/src/routes/settings.ts — deliberately without `fileId`, an
 * internal storage identifier that never crosses the wire. filename/mimeType
 * /size stay nullable for guides migrated from the old flat per-shop shape,
 * which had them nullable too.
 */
export interface SizeGuideEntryView {
  id: string;
  name: string;
  categories: SizeGuideCategories;
  filename: string | null;
  mimeType: string | null;
  size: number | null;
  structuredSizeGuide: StructuredSizeGuide;
  uploadedAt: string;
}

/**
 * Outcome of the last size guide coverage check
 * (services/sizeGuideCoverage.ts on the backend). Advisory only: a failed
 * check reports `error`, it never blocks a save.
 */
export interface SizeCoverageReport {
  checkedAt: string;
  guides: Array<{ id: string; matched: number; sampled: number; examples: string[]; invalidProductIds?: string[] }>;
  uncovered: { count: number; sampled: number; examples: string[] };
  error?: string | null;
}

/**
 * Merchant settings for rate limiting and other configurations.
 *
 * Mirrors settingsResponseData() in web/backend/src/routes/settings.ts, which
 * is a whitelist rather than the raw document: each size guide's `fileId` is
 * internal storage and never crosses the wire (see SizeGuideEntryView).
 */
export interface MerchantSettings {
  rateLimiting: {
    enabled: boolean;
    maxGenerationsPerVisitor: number;
    cooldownMinutes: number;
  };
  targeting: Targeting;
  fitPreferenceEnabled: boolean;
  defaultFitPreference: FitPreference;
  /** Optional for older API responses; the backend now always sends an array. */
  fitOverrides?: FitOverride[];
  sizeGuides: SizeGuideEntryView[];
  sizeCoverageReport: SizeCoverageReport | null;
  /** Last time `targeting` reached the storefront metafield, or null. */
  targetingSyncedAt: string | null;
  /** Why the last mirror attempt failed, or null when it succeeded. */
  targetingSyncError: string | null;
}

/** What a settings PUT may carry: everything else on the document is
 * untouched, and an absent `targeting` leaves the stored rules alone. */
export interface MerchantSettingsUpdate {
  rateLimiting: MerchantSettings['rateLimiting'];
  targeting?: Targeting;
  defaultFitPreference?: FitPreference;
  fitOverrides?: FitOverride[];
}

export interface FitOverride {
  categoryId: string;
  categoryLabel: string;
  preference: FitPreference;
  priority: number;
}

/**
 * Merchant onboarding flow (dashboard demo). Mirrors
 * web/backend/src/routes/onboarding.ts stateResponseData().
 */
export type OnboardingFlowStep =
  | 'product'
  | 'photo'
  | 'result'
  | 'simulation'
  | 'activation'
  | 'done';

export type OnboardingStatus = 'active' | 'completed';
export type OnboardingFlowEntry = 'new_install' | 'existing_merchant';

export interface OnboardingStateData {
  version: number;
  status: OnboardingStatus;
  flowEntry: OnboardingFlowEntry;
  step: OnboardingFlowStep;
  selectedProductId: string | null;
  demo: {
    attempts: number;
    succeeded: number;
    remaining: number;
    firstResultAt: string | null;
  };
  activation: {
    verified: boolean;
    verifiedAt: string | null;
    blockType: string | null;
  };
}

export interface OnboardingProduct {
  id: string;
  title: string;
  imageUrl: string | null;
  price: string | null;
  currencyCode: string | null;
  eligible: boolean;
  ineligibleReason: 'no_image' | 'not_active' | null;
  risky: boolean;
}

export interface OnboardingProductsData {
  products: OnboardingProduct[];
  autoSelectedId: string | null;
  selectedId: string | null;
  riskyOnly: boolean;
}

/** Normalized demo failure, mirroring the backend's reason enum. */
export type DemoFailureReason =
  | 'flagged'
  | 'cannot_generate'
  | 'invalid_photo'
  | 'payload_too_large'
  | 'image_unavailable'
  | 'engine_unavailable'
  | 'timeout'
  | 'product_not_found'
  | 'allowance_exhausted'
  | 'attempts_exhausted'
  | 'too_soon'
  | 'no_state'
  | 'unexpected';

export interface DemoError {
  reason: DemoFailureReason;
  retryable: boolean;
  switchProduct: boolean;
  nextProduct?: OnboardingProduct | null;
}

export interface DemoSuccessData {
  image: string;
  /** The result's actual format, sniffed server-side from its bytes. */
  imageMimeType?: 'image/png' | 'image/jpeg';
  cached: boolean;
  durationMs: number;
  product: {
    id: string;
    title: string;
    imageUrl: string | null;
    price: string | null;
    currencyCode: string | null;
  };
  demo: OnboardingStateData['demo'] | null;
}

export interface ActivationCheckData {
  active: boolean;
  blockType: string | null;
  disabledOnly?: boolean;
  themeName?: string | null;
  /** The app identity the server matched blocks against (diagnostics). */
  appHandle?: string;
  reason?: string;
  state?: OnboardingStateData | null;
}
